import { getAvatarUrl } from "common/avatar"
import { AttachmentReference, CompaniesSchema, CompaniesSchemaWithId } from "components/companies/companies.schema"
import { ProjectInfo } from "components/hub/manageCompanies/manageCompanies"
import { CompaniesInProject, ProjectSchemaWithId } from "components/project/project.schema"
import { getProjectLogoSync } from "components/project/project.utils"
import Vue from "vue"
import { Component } from "vue-property-decorator"
/**
 * Get a Company's name from Id
 *
 * @param companyId The company id
 * @param companies The list of companies to search
 * @returns The company's name or empty string
 */
export function companyName(companyId: number, companies: CompaniesSchema[]): string {
    const companyRecord = companies.find((companyRecord) => companyRecord.id === companyId)
    return companyRecord ? companyRecord.data.name : ""
}

/**
 * Get company logo or default logo with company string
 *
 * @param company Company for which logo is accessed
 * @param color Company color
 * @param base64 if true, Returns base64 logo string
 * @param isCompanyLogoAvailable
 * @param project
 * @returns string logo
 */
export function getCompanyLogo(
    company: CompaniesSchema,
    color?: string,
    base64?: boolean,
    project?: ProjectSchemaWithId
): {
    url: string
    isSvg: boolean
} {
    const logos: AttachmentReference[] = company.data.attachments.filter((attachment) => attachment.tags.indexOf("logo") !== -1)

    if (logos.length > 0) {
        const attachmentStreamUrl = `${process.env.FLYPAPER_HOST}/attachments/thumbnail/${logos[0].id}`
        return { url: attachmentStreamUrl, isSvg: false }
    } else {
        return {
            url: getAvatarUrl(company.data.name, color),
            isSvg: !base64
        }
    }
}

/**
 * Get all projects which have this company
 *
 * @param companyId Company id to search for
 * @param allProjects All projects
 * @returns project info array
 */
export function getActiveProjectsForCompany(companyId: number, allProjects: Array<ProjectSchemaWithId>): Array<ProjectInfo> {
    const projects: Array<ProjectInfo> = []
    allProjects.forEach((project) => {
        if (project.data.companies_in_project.some((company) => company.company_id === companyId)) {
            projects.push({
                id: project.id,
                name: project.data.name,
                logo: getProjectLogoSync(project.id, allProjects)
            })
        }
    })

    return projects
}

/**
 * Get Logo for a given company from project
 *
 * @param name Name of company
 * @param companies_in_project Companies in current project
 * @param allCompanies All Companies
 * @param project
 * @param color Company color in project
 * @param base64 boolean if we want base64
 * @param size logo size
 * @returns Company logo string
 */
export function getLogoForCompany(
    name: string,
    companies_in_project: CompaniesInProject[],
    allCompanies: CompaniesSchemaWithId[],
    color?: string,
    base64?: boolean,
    size?: number
): {
    url: string
    isSvg: boolean
} {
    const company = companies_in_project.find((comp) => companyName(comp.company_id, allCompanies) === name)
    if (company) {
        return getCompanyAvatar(company, allCompanies, undefined, color, base64, size)
    } else {
        return {
            url: "",
            isSvg: false
        }
    }
}

/**
 * Get company logo
 *
 * @param company Company for which logo is accessed
 * @param project
 * @param allCompanies All companies
 * @param color Company color
 * @param base64 optional boolean if we need base64
 * @param size logo size
 * @returns string avatar
 */
export function getCompanyAvatar(
    company: CompaniesInProject,
    allCompanies: CompaniesSchemaWithId[],
    project?: ProjectSchemaWithId,
    color?: string,
    base64?: boolean,
    size?: number
): {
    url: string
    isSvg: boolean
} {
    if (company.project_logo) {
        return {
            url: size
                ? `${process.env.FLYPAPER_HOST}/attachments/thumbnail/${company.project_logo.id}?size=${size}`
                : `${process.env.FLYPAPER_HOST}/attachments/thumbnail/${company.project_logo.id}`,
            isSvg: false
        }
    } else {
        const companyData = allCompanies.find((comp) => comp.id === company.company_id)

        // not loaded yet / not in this hub: no name to seed an avatar with, and callers (e.g. print) treat "" as "no logo"
        if (!companyData) return { url: "", isSvg: false }

        // Never falls back to the hub company's logo: the hub logo is copied into project_logo when the company is
        // imported, after which the two are separate - a project logo the user removed, or a hub logo added after
        // the import, must not show inside the project. Same generated avatar getCompanyLogo uses for no logo.
        return {
            url: getAvatarUrl(companyData.data.name, company.color),
            isSvg: !base64
        }
    }
}

/**
 * Drops the companies whose save just succeeded from a pending-save queue. A company edited again while its
 * request was in flight is the same (mutable) object that was sent, so it is matched by edit version rather
 * than by reference - it stays queued so the newer edit is saved next time instead of being silently dropped.
 *
 * @param queue the current pending-save queue (may include companies queued while the save was in flight)
 * @param sentVersions edit version of each company at the moment its save request was sent
 * @param currentVersions edit version of each company now
 * @param failedIds ids of companies whose save request failed
 * @returns the queue with only the saved-and-unchanged companies removed
 */
export function getCompaniesStillPendingSave<T extends { id: number }>(
    queue: T[],
    sentVersions: Map<number, number>,
    currentVersions: Map<number, number>,
    failedIds: Set<number>
): T[] {
    return queue.filter((company) => {
        const sentVersion = sentVersions.get(company.id)
        if (sentVersion === undefined || failedIds.has(company.id)) return true
        return (currentVersions.get(company.id) ?? 0) !== sentVersion
    })
}

@Component
export class CompanyUtilsMixin extends Vue {
    public companyName(companyId: number, companies: CompaniesSchema[]): string {
        return companyName(companyId, companies)
    }

    public getCompanyLogo(
        company: CompaniesSchema,
        color?: string,
        base64?: boolean,
        project?: ProjectSchemaWithId
    ): {
        url: string
        isSvg: boolean
    } {
        return getCompanyLogo(company, color, base64, project)
    }

    public getActiveProjectsForCompany(companyId: number, allProjects: Array<ProjectSchemaWithId>): Array<ProjectInfo> {
        return getActiveProjectsForCompany(companyId, allProjects)
    }

    public getLogoForCompany(
        name: string,
        companies_in_project: CompaniesInProject[],
        allCompanies: CompaniesSchemaWithId[],
        color?: string,
        base64?: boolean
    ): {
        url: string
        isSvg: boolean
    } {
        return getLogoForCompany(name, companies_in_project, allCompanies, color, base64)
    }

    public getCompanyAvatar(
        company: CompaniesInProject,
        allCompanies: CompaniesSchemaWithId[],
        project?: ProjectSchemaWithId,
        color?: string,
        base64?: boolean,
        size?: number
    ): {
        url: string
        isSvg: boolean
    } {
        return getCompanyAvatar(company, allCompanies, project, color, base64, size)
    }
}
